import React, { useState, useEffect, ChangeEvent } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { categories, Category, SubCategory, findCategoryAndSubcategory } from '../../../routes/convertRoute';
import SEO from './../../../components/SEO';
// import { trackEvent } from './../../../utils/GoogleAnalytics';
import { pushGTMEvent } from '../../../utils/GoogleTagManager';
import DataConverterSVG from './converters/DataConverterSvg';

// Import components directly 
import JsonToExcel from './converters/Json/JsonToExcel';
import JsonToSql from './converters/Json/JsonToSql';
import JsonToCsv from './converters/Json/JsonToCvs';
import JsonToXml from './converters/Json/JsonToXml';
import JsonToPdf from './converters/Json/JsonToPdf';
import JsonToHtml from './converters/Json/JsonToHtml';
import JsonToYaml from './converters/Json/JsonToYaml';
import JsonToMarkdown from './converters/Json/JsonToMarkdown';
import JsonToTypeScript from './converters/Json/JsonToTypeScript';
import JsonToCSharp from './converters/Json/JsonToCSharp';
import JsonToJava from './converters/Json/JsonToJava';
import JsonToPython from './converters/Json/JsonToPython';
import JsonToGo from './converters/Json/JsonToGo';
import JsonToSwift from './converters/Json/JsonToSwift';
import JsonToKotlin from './converters/Json/JsonToKotlin';
import JsonToProtobuf from './converters/Json/JsonToProtobuf';
import JsonToGraphQL from './converters/Json/JsonToGraphQL';
import JsonToBson from './converters/Json/JsonToBson';
import JsonToAvro from './converters/Json/JsonToAvro';
import JsonToSchema from './converters/Json/JsonToSchema';
import JsonToParquet from './converters/Json/JsonToParquet';
import JsonToToml from './converters/Json/JsonToToml';
import JsonToRss from './converters/Json/JsonToRss';
import JsonToIcal from './converters/Json/JsonToIcal';
import JsonToSvg from './converters/Json/JsonToSvg';
import JsonToChart from './converters/Json/JsonToChart';
import JsonToLatex from './converters/Json/JsonToLatex';
import JsonToMongoDB from './converters/Json/JsonToMongoDB';

// SQL
import SqlToJson from './converters/Sql/SqlToJson';
import SqlToCsv from './converters/Sql/SqlToCsv';
import SqlToExcel from './converters/Sql/SqlToExcel';
import SqlToXml from './converters/Sql/SqlToXml';
import SqlToYaml from './converters/Sql/SqlToYaml';
import SqlToHtml from './converters/Sql/SqlToHtml';
import SqlToMarkdown from './converters/Sql/SqlToMarkdown';
import SqlToText from './converters/Sql/SqlToText';
import SqlToSqlite from './converters/Sql/SqlToSqlite';
import SqlToMongoDB from './converters/Sql/SqlToMongoDB';
import SqlToGraphql from './converters/Sql/SqlToGraphql';
import SqlToPhpArray from './converters/Sql/SqlToPhpArray';
import SqlToPython from './converters/Sql/SqlToPython';
import SqlToJs from './converters/Sql/SqlToJs';
import SqlToApi from './converters/Sql/SqlToApi';
import SqlToErDiagram from './converters/Sql/SqlToErDiagram';

// CSV
import CsvToJson from './converters/Cvs/CsvToJson';
import CsvToSql from './converters/Cvs/CsvToSql';
import CsvToExcel from './converters/Cvs/CsvToExcel';
import CsvToXml from './converters/Cvs/CsvToXml';
import CsvToYaml from './converters/Cvs/CsvToYaml';
import CsvToHtml from './converters/Cvs/CsvToHtml';
import CsvToMarkdown from './converters/Cvs/CsvToMarkdown';
import CsvToGraphQL from './converters/Cvs/CsvToGraphQL';
import CsvToJsonl from './converters/Cvs/CsvToJsonl';
import CsvToText from './converters/Cvs/CsvToText';

// XML
import XmlToJson from './converters/Xml/XmlToJson';
import XmlToCsv from './converters/Xml/XmlToCsv';
import XmlToHtml from './converters/Xml/XmlToHtml';
import XmlToYaml from './converters/Xml/XmlToYaml';
import XmlToSql from './converters/Xml/XmlToSql';
import XmlToMarkdown from './converters/Xml/XmlToMarkdown';
import XmlToXsd from './converters/Xml/XmlToXsd';

// Yaml
import YamlToJson from './converters/Yaml/YamlToJson';
import YamlToXml from './converters/Yaml/YamlToXml';
import YamlToCsv from './converters/Yaml/YamlToCsv';

// PDF
import PDFToExcel from './converters/Pdf/PDFToExcel';
import PDFToJson from './converters/Pdf/PDFToJson';
import PDFToXML from './converters/Pdf/PDFToXML';
import PDFToText from './converters/Pdf/PDFToText';
import PDFToWord from './converters/Pdf/PDFToWord';

import AnimatedCard from './Search';
import { ArrowRight, ChevronRight, Search } from 'lucide-react';
import { useUserTracking } from './../../../contexts/UserTrackingContext';

// Type definitions
type ConverterComponentsType = {
  [category: string]: {
    [subcategory: string]: React.ComponentType<any>;
  }
};

// Direct route mapping (new)
type DirectRouteMapType = {
  [path: string]: {
    categoryId: string;
    subcategoryId: string;
    component: React.ComponentType<any>;
  }
};

type SubCategoryType = {
  id: string;
  name: string;
  path: string;
};

type CategoryType = {
  id: string;
  name: string;
  icon: string;
  isOpen: boolean;
  subCategories: SubCategory[];
};

// Component map for category/subcategory structure
const converterComponents: ConverterComponentsType = {
  'json': {
    'json-to-excel': JsonToExcel,
    'json-to-sql': JsonToSql,
    'json-to-csv': JsonToCsv,
    'json-to-xml': JsonToXml,
    'json-to-pdf': JsonToPdf,
    'json-to-html': JsonToHtml,
    'json-to-yaml': JsonToYaml,
    'json-to-markdown': JsonToMarkdown,
    'json-to-typescript': JsonToTypeScript,
    'json-to-csharp': JsonToCSharp,
    'json-to-java': JsonToJava,
    'json-to-python': JsonToPython,
    'json-to-go': JsonToGo,
    'json-to-swift': JsonToSwift,
    'json-to-kotlin': JsonToKotlin,
    'json-to-protobuf': JsonToProtobuf,
    'json-to-graphql': JsonToGraphQL,
    'json-to-bson': JsonToBson,
    'json-to-avro': JsonToAvro,
    'json-to-schema': JsonToSchema,
    'json-to-parquet': JsonToParquet,
    'json-to-toml': JsonToToml,
    'json-to-rss': JsonToRss,
    'json-to-ical': JsonToIcal,
    'json-to-svg': JsonToSvg,
    'json-to-chart': JsonToChart,
    'json-to-latex': JsonToLatex,
    'json-to-mongodb': JsonToMongoDB,
  },
  'sql': {
    'sql-to-json': JsonToSql,
    'sql-to-csv': SqlToCsv,
    'sql-to-excel': SqlToExcel,
    'sql-to-xml': SqlToXml,
    'sql-to-yaml': SqlToYaml,
    'sql-to-html': SqlToHtml,
    'sql-to-markdown': SqlToMarkdown,
    'sql-to-text': SqlToText,
    'sql-to-sqlite': SqlToSqlite,
    'sql-to-mongodb': SqlToMongoDB,
    'sql-to-graphql': SqlToGraphql,
    'sql-to-php-array': SqlToPhpArray,
    'sql-to-python': SqlToPython,
    'sql-to-js': SqlToJs,
    'sql-to-api': SqlToApi,
    'sql-to-er-diagram': SqlToErDiagram,
  },
  'csv': {
    'csv-to-json': CsvToJson,
    'csv-to-sql': CsvToSql,
    'csv-to-excel': CsvToExcel,
    'csv-toxml': CsvToXml,
    'csv-to-yaml': CsvToYaml,
    'csv-to-html': CsvToHtml,
    'csv-to-markdown': CsvToMarkdown,
    'csv-to-graphql': CsvToGraphQL,
    'csv-to-jsonl': CsvToJsonl,
    'csv-to-text': CsvToText
  },
  'xml': {
    'xml-to-json': XmlToJson,
    'xml-to-csv': XmlToCsv,
    'xml-to-html': XmlToHtml,
    'xml-to-yaml': XmlToYaml,
    'xml-to-sql': XmlToSql,
    'xml-to-markdown': XmlToMarkdown,
    'xml-to-xsd': XmlToXsd
  },
  'yaml': {
    'yaml-to-json': YamlToJson,
    'yaml-to-xml': YamlToXml,
    'yaml-to-csv': YamlToCsv,
  },
  'pdf': {
    'pdf-to-excel': PDFToExcel,
    'pdf-to-json': PDFToJson,
    'pdf-to-xml': PDFToXML,
    'pdf-to-text': PDFToText,
    'pdf-to-word': PDFToWord,
  }
};

// Direct route mapping for simplified URLs (new)
const directRouteMap: DirectRouteMapType = {
  'json-to-excel': { categoryId: 'json', subcategoryId: 'json-to-excel', component: JsonToExcel },
  'json-to-sql': { categoryId: 'json', subcategoryId: 'json-to-sql', component: JsonToSql },
  'json-to-csv': { categoryId: 'json', subcategoryId: 'json-to-csv', component: JsonToCsv },
  'json-to-xml': { categoryId: 'json', subcategoryId: 'json-to-xml', component: JsonToXml },
  'json-to-pdf': { categoryId: 'json', subcategoryId: 'json-to-pdf', component: JsonToPdf },
  'json-to-html': { categoryId: 'json', subcategoryId: 'json-to-html', component: JsonToHtml },
  'json-to-yaml': { categoryId: 'json', subcategoryId: 'json-to-yaml', component: JsonToYaml },
  'json-to-markdown': { categoryId: 'json', subcategoryId: 'json-to-markdown', component: JsonToMarkdown },
  'json-to-typescript': { categoryId: 'json', subcategoryId: 'json-to-typescript', component: JsonToTypeScript },
  'json-to-csharp': { categoryId: 'json', subcategoryId: 'json-to-csharp', component: JsonToCSharp },
  'json-to-java': { categoryId: 'json', subcategoryId: 'json-to-java', component: JsonToJava },
  'json-to-python': { categoryId: 'json', subcategoryId: 'json-to-python', component: JsonToPython },
  'json-to-go': { categoryId: 'json', subcategoryId: 'json-to-go', component: JsonToGo },
  'json-to-swift': { categoryId: 'json', subcategoryId: 'json-to-swift', component: JsonToSwift },
  'json-to-kotlin': { categoryId: 'json', subcategoryId: 'json-to-kotlin', component: JsonToKotlin },
  'json-to-protobuf': { categoryId: 'json', subcategoryId: 'json-to-protobuf', component: JsonToProtobuf },
  'json-to-graphql': { categoryId: 'json', subcategoryId: 'json-to-graphql', component: JsonToGraphQL },
  'json-to-bson': { categoryId: 'json', subcategoryId: 'json-to-bson', component: JsonToBson },
  'json-to-avro': { categoryId: 'json', subcategoryId: 'json-to-avro', component: JsonToAvro },
  'json-to-schema': { categoryId: 'json', subcategoryId: 'json-to-schema', component: JsonToSchema },
  'json-to-parquet': { categoryId: 'json', subcategoryId: 'json-to-parquet', component: JsonToParquet },
  'json-to-toml': { categoryId: 'json', subcategoryId: 'json-to-toml', component: JsonToToml },
  'json-to-rss': { categoryId: 'json', subcategoryId: 'json-to-rss', component: JsonToRss },
  'json-to-ical': { categoryId: 'json', subcategoryId: 'json-to-ical', component: JsonToIcal },
  'json-to-svg': { categoryId: 'json', subcategoryId: 'json-to-svg', component: JsonToSvg },
  'json-to-chart': { categoryId: 'json', subcategoryId: 'json-to-chart', component: JsonToChart },
  'json-to-latex': { categoryId: 'json', subcategoryId: 'json-to-latex', component: JsonToLatex },
  'json-to-mongodb': { categoryId: 'json', subcategoryId: 'json-to-mongodb', component: JsonToMongoDB },

  // SQL
  'sql-to-json': { categoryId: 'sql', subcategoryId: 'sql-to-json', component: SqlToJson },
  'sql-to-csv': { categoryId: 'sql', subcategoryId: 'sql-to-csv', component: SqlToCsv },
  'sql-to-excel': { categoryId: 'sql', subcategoryId: 'sql-to-excel', component: SqlToExcel },
  'sql-to-xml': { categoryId: 'sql', subcategoryId: 'sql-to-xml', component: SqlToXml },
  'sql-to-yaml': { categoryId: 'sql', subcategoryId: 'sql-to-yaml', component: SqlToYaml },
  'sql-to-html': { categoryId: 'sql', subcategoryId: 'sql-to-html', component: SqlToHtml },
  'sql-to-markdown': { categoryId: 'sql', subcategoryId: 'sql-to-markdown', component: SqlToMarkdown },
  'sql-to-text': { categoryId: 'sql', subcategoryId: 'sql-to-text', component: SqlToText },
  'sql-to-sqlite': { categoryId: 'sql', subcategoryId: 'sql-to-sqlite', component: SqlToSqlite },
  'sql-to-mongodb': { categoryId: 'sql', subcategoryId: 'sql-to-mongodb', component: SqlToMongoDB },
  'sql-to-graphql': { categoryId: 'sql', subcategoryId: 'sql-to-graphql', component: SqlToGraphql },
  'sql-to-php-array': { categoryId: 'sql', subcategoryId: 'sql-to-php-array', component: SqlToPhpArray },
  'sql-to-python': { categoryId: 'sql', subcategoryId: 'sql-to-python', component: SqlToPython },
  'sql-to-js': { categoryId: 'sql', subcategoryId: 'sql-to-js', component: SqlToJs },
  'sql-to-api': { categoryId: 'sql', subcategoryId: 'sql-to-api', component: SqlToApi },
  'sql-to-er-diagram': { categoryId: 'sql', subcategoryId: 'sql-to-er-diagram', component: SqlToErDiagram },

  // CSV
  'csv-to-json': { categoryId: 'csv', subcategoryId: 'csv-to-json', component: CsvToJson },
  'csv-to-sql': { categoryId: 'csv', subcategoryId: 'csv-to-sql', component: CsvToSql },
  'csv-to-excel': { categoryId: 'csv', subcategoryId: 'csv-to-excel', component: CsvToExcel },
  'csv-to-xml': { categoryId: 'csv', subcategoryId: 'csv-to-xml', component: CsvToXml },
  'csv-to-yaml': { categoryId: 'csv', subcategoryId: 'csv-to-yaml', component: CsvToYaml },
  'csv-to-html': { categoryId: 'csv', subcategoryId: 'csv-to-html', component: CsvToHtml },
  'csv-to-markdown': { categoryId: 'csv', subcategoryId: 'csv-to-markdown', component: CsvToMarkdown },
  'csv-to-graphql': { categoryId: 'csv', subcategoryId: 'csv-to-graphql', component: CsvToGraphQL },
  'csv-to-jsonl': { categoryId: 'csv', subcategoryId: 'csv-to-jsonl', component: CsvToJsonl },
  'csv-to-text': { categoryId: 'csv', subcategoryId: 'csv-to-text', component: CsvToText },

  // XML
  'xml-to-json': { categoryId: 'xml', subcategoryId: 'xml-to-json', component: XmlToJson },
  'xml-to-csv': { categoryId: 'xml', subcategoryId: 'xml-to-csv', component: XmlToCsv },
  'xml-to-html': { categoryId: 'xml', subcategoryId: 'xml-to-html', component: XmlToHtml },
  'xml-to-yaml': { categoryId: 'xml', subcategoryId: 'xml-to-yaml', component: XmlToYaml },
  'xml-to-sql': { categoryId: 'xml', subcategoryId: 'xml-to-sql', component: XmlToSql },
  'xml-to-markdown': { categoryId: 'xml', subcategoryId: 'xml-to-markdown', component: XmlToMarkdown },
  'xml-to-xsd': { categoryId: 'xml', subcategoryId: 'xml-to-xsd', component: XmlToXsd },

  // YAML
  'yaml-to-json': { categoryId: 'yaml', subcategoryId: 'yaml-to-json', component: YamlToJson },
  'yaml-to-xml': { categoryId: 'yaml', subcategoryId: 'yaml-to-xml', component: YamlToXml },
  'yaml-to-csv': { categoryId: 'yaml', subcategoryId: 'yaml-to-csv', component: YamlToCsv },

  // PDF
  'pdf-to-excel': { categoryId: 'pdf', subcategoryId: 'pdf-to-excel', component: PDFToExcel },
  'pdf-to-json': { categoryId: 'pdf', subcategoryId: 'pdf-to-json', component: PDFToJson },
  'pdf-to-xml': { categoryId: 'pdf', subcategoryId: 'pdf-to-xml', component: PDFToXML },
  'pdf-to-text': { categoryId: 'pdf', subcategoryId: 'pdf-to-text', component: PDFToText },
  'pdf-to-word': { categoryId: 'pdf', subcategoryId: 'pdf-to-word', component: PDFToWord },
};

// Loading component
const LoadingFallback = () => (
  <div className="flex items-center justify-center h-full">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
  </div>
);

// Fallback component when no converter is found
const ConverterNotFound = ({ categoryId, subcategoryId }: { categoryId?: string, subcategoryId?: string }) => (
  <div className="max-w-4xl mx-auto">
    <h1 className="text-2xl font-bold mb-6">Converter Not Found</h1>
    <div className="p-4 mb-6 bg-yellow-50 border border-yellow-200 text-yellow-700 rounded-md">
      <p className="mb-2">
        <strong>The requested converter could not be found.</strong>
      </p>
      <p>
        We couldn't find a converter for {categoryId}/{subcategoryId}.
      </p>
    </div>
  </div>
);

const ConvertData: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const ScrollToTop = () => {
    useEffect(() => {
      // Scroll to top when component mounts
      window.scrollTo(0, 0);
    }, []);

    return null; // This component doesn't render anything
  };

  const { trackEvent } = useUserTracking();
  const [query, setQuery] = useState<string>('');
  const [filteredData, setFilteredData] = useState<Category[]>([]);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toLowerCase();
    setQuery(value);

    if (value === '') {
      setFilteredData([]);
      return;
    }

    const results = categories
      .map(category => {
        const matchingSubCategories = category.subCategories.filter(sub =>
          sub.name.toLowerCase().includes(value)
        );
        if (matchingSubCategories.length > 0) {
          return {
            ...category,
            subCategories: matchingSubCategories
          };
        }
        return null;
      })
      .filter((cat): cat is Category => cat !== null);

    setFilteredData(results);
  };

  // Check if we're using a direct route first
  const directPath = location.pathname.split('/').pop() || '';
  const isDirectRoute = directPath in directRouteMap;

  // Extract route parameters based on URL pattern
  const urlParts = location.pathname.split('/').filter(Boolean);

  // If using direct route, extract the category/subcategory from our mapping
  const categoryId = isDirectRoute
    ? directRouteMap[directPath].categoryId
    : (urlParts.length > 1 ? urlParts[1] : '');

  const subcategoryId = isDirectRoute
    ? directRouteMap[directPath].subcategoryId
    : (urlParts.length > 2 ? urlParts[2] : '');

  // Log the URL and extracted parameters for debugging
  useEffect(() => {
    console.log('Current URL:', location.pathname);
    console.log('URL parts:', urlParts);
    console.log('Is direct route:', isDirectRoute);
    console.log('Direct path:', directPath);
    console.log('Extracted parameters:', { categoryId, subcategoryId });
  }, [location.pathname]);

  // This controls whether the sidebar is visible or not
  const [sidebarOpen, setSidebarOpen] = useState(true);

  // Check if we're on mobile
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);

  // Listen for window resize events
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // State for categories with their expansion status
  const [categoriesState, setCategoriesState] = useState<Category[]>(() => {
    // Initialize with categories from the routes file and set isOpen based on current route
    return categories.map(category => ({
      ...category,
      isOpen: category.id === categoryId || Boolean(category.isOpen)
    }));
  });

  // Update categories isOpen state when URL params change
  useEffect(() => {
    setCategoriesState(prevCategories =>
      prevCategories.map(category => ({
        ...category,
        isOpen: category.id === categoryId || Boolean(category.isOpen)
      }))
    );
  }, [categoryId]);

  // Toggle category expansion
  const toggleCategory = (catId: string) => {
    setCategoriesState(prevState =>
      prevState.map(category =>
        category.id === catId
          ? { ...category, isOpen: !Boolean(category.isOpen) }
          : category
      )
    );
  };

  // Navigate to a subcategory - UPDATED to use the direct routes
  const navigateToSubcategory = (categoryId: string, subcategoryId: string, name: string) => {
    trackEvent('convert_page', {
      event_category: 'conversion',
      event_label: 'Navigate to Subcategory',
      page_location: window.location.href,
    });

    // Find the direct path for this category/subcategory combination
    const directPath = Object.entries(directRouteMap).find(
      ([_, value]) => value.categoryId === categoryId && value.subcategoryId === subcategoryId
    );

    let targetPath;
    if (directPath) {
      // Use the simplified URL if we found a mapping
      targetPath = `/${directPath[0]}`;
      navigate(targetPath);
    } else {
      // Fall back to the old URL structure if no mapping exists
      targetPath = `/convert/${categoryId}/${subcategoryId}`;
      navigate(targetPath);
    }

    // Close sidebar on mobile after navigation
    if (isMobile) {
      setSidebarOpen(false);
    }

    // Force update title and send analytics after navigation
    setTimeout(() => {
      const { category, subcategory } = findCategoryAndSubcategory(categoryId, subcategoryId) || {};
      
      if (category && subcategory) {
        const toolName = subcategory.name;
        const pageTitle = `${toolName} - Free Online Data Conversion Tool`;
        
        // Update title
        document.title = pageTitle;
        
        // Update meta description
        let description = "";
        if (toolName.includes(" to ")) {
          const [from, to] = toolName.split(" to ");
          description = `Convert ${from} data to ${to} format online for free. Easy-to-use ${from} to ${to} converter with no installation required.`;
        } else {
          description = `Work with ${category.name} data using our free online ${toolName} tool. No signup required, instant results.`;
        }
        
        const metaDescription = document.querySelector('meta[name="description"]');
        if (metaDescription) {
          metaDescription.setAttribute('content', description);
        }
        
        // Send GTM event with updated info
        pushGTMEvent({
          event: 'page_view',
          page_title: pageTitle,
          page_location: window.location.origin + targetPath,
          page_path: targetPath,
          page_category: category.name,
          page_subcategory: subcategory.name,
          navigation_type: 'sidebar_click'
        });
      }
    }, 200);
  };

  // Find current selected items using helper from routes file
  const {
    category: currentCategory = null,
    subcategory: currentSubcategory = null
  } = findCategoryAndSubcategory(categoryId, subcategoryId) || {};

  // Get display name for the current page
  const getPageTitle = () => {
    if (currentCategory && currentSubcategory) {
      return currentSubcategory.name;
    }
    return 'Data Converter';
  };

  // Function to check if a category is the currently selected one
  const isCategorySelected = (category: Category) => {
    return category.id === categoryId;
  };

  // Function to check if a subcategory is the currently selected one
  const isSubcategorySelected = (catId: string, subCatId: string) => {
    // Check both URL patterns
    return location.pathname.includes(`/convert/${catId}/${subCatId}`) ||
      Object.entries(directRouteMap).some(
        ([path, mapping]) =>
          location.pathname.includes(`/${path}`) &&
          mapping.categoryId === catId &&
          mapping.subcategoryId === subCatId
      );
  };

  const generateSEOMetadata = () => {
    if (currentCategory && currentSubcategory) {
      const toolName = currentSubcategory.name;
      const pageTitle = `${toolName} - Free Online Data Conversion Tool`;

      let description = "";
      if (toolName.includes(" to ")) {
        const [from, to] = toolName.split(" to ");
        description = `Convert ${from} data to ${to} format online for free. Easy-to-use ${from} to ${to} converter with no installation required.`;
      } else if (toolName.includes("Formatter")) {
        const format = toolName.split(" ")[0];
        description = `Format and beautify your ${format} data with our free online ${format} formatter. Clean, indent, and structure your ${format} for better readability.`;
      } else {
        description = `Work with ${currentCategory.name} data using our free online ${toolName} tool. No signup required, instant results.`;
      }

      const keywords = `${toolName.toLowerCase()}, ${toolName.toLowerCase().replace(" to ", " converter, ")}, ${currentCategory.name.toLowerCase()} converter, online converter`;

      return {
        title: pageTitle,
        description: description,
        keywords: keywords,
        canonicalUrl: window.location.href,
        ogTitle: pageTitle,
        ogDescription: description,
        ogType: "website",
        twitterCard: "summary_large_image",
        twitterTitle: pageTitle,
        twitterDescription: description
      };
    } else if (currentCategory) {
      // Category page
      const pageTitle = `${currentCategory.name} Converters - Free Online Tools`;
      const description = `Free online tools to convert ${currentCategory.name} data to various formats. Convert, format and transform your ${currentCategory.name} data with ease.`;
      const keywords = `${currentCategory.name.toLowerCase()} converters, ${currentCategory.name.toLowerCase()} formatter, ${currentCategory.name.toLowerCase()} tools, online converter`;

      return {
        title: pageTitle,
        description: description,
        keywords: keywords,
        canonicalUrl: window.location.href,
        ogTitle: pageTitle,
        ogDescription: description
      };
    }

    // Default for home page
    return {
      title: "Data Converters - Free Online Data Format Conversion Tools",
      description: "Transform your data between various formats including JSON, CSV, SQL, XML, and YAML with our free online data converters.",
      keywords: "data converter, json to csv, csv to json, sql formatter, xml formatter, yaml converter",
      canonicalUrl: window.location.href
    };
  };

  const seoProps = generateSEOMetadata();

  // NEW: Critical useEffect for fixing SPA analytics and title updates
  useEffect(() => {
    const seoData = generateSEOMetadata();
    
    // Update document title immediately
    document.title = seoData.title;
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', seoData.description);
    }
    
    // Update meta keywords if exists
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', seoData.keywords);
    }
    
    // Send manual page view to GTM/GA4 with correct title
    setTimeout(() => {
      pushGTMEvent({
        event: 'page_view',
        page_title: seoData.title,
        page_location: window.location.href,
        page_path: location.pathname,
        page_category: currentCategory?.name || 'Home',
        page_subcategory: currentSubcategory?.name || '',
        navigation_type: 'route_change'
      });
    }, 100); // Small delay to ensure title is updated first
    
  }, [location.pathname, currentCategory, currentSubcategory]);

  // Render the main content area based on the current state - UPDATED to support direct routes
  const renderMainContent = () => {
    // console.log('Rendering main content with:', { categoryId, subcategoryId, isDirectRoute, directPath });

    // First check if we're using a direct route
    if (isDirectRoute && directRouteMap[directPath]) {
      const DirectComponent = directRouteMap[directPath].component;
      return <DirectComponent />;
    }

    // Otherwise fall back to the original logic for compatibility
    if (categoryId && subcategoryId) {
      // Check if the converter exists in our component map
      const converterCategory = converterComponents[categoryId];
      if (converterCategory && converterCategory[subcategoryId]) {
        const ConverterComponent = converterCategory[subcategoryId];
        return <ConverterComponent />;
      } else {
        console.warn(`Converter not found for ${categoryId}/${subcategoryId}`);
        return <ConverterNotFound categoryId={categoryId} subcategoryId={subcategoryId} />;
      }
    }

    // Default home content or category landing page
    return (
      <div className="min-h-screen from-gray-50 to-gray-100">

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Search bar */}
          <div className="max-w-lg mx-auto mb-12 relative">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search size={20} className="text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search data converters..."
                value={query}
                onChange={handleInputChange}
                className="w-full pl-10 pr-4 py-3 text-base rounded-xl border border-gray-300 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white transition-all duration-200"
              />
            </div>

            {filteredData?.length > 0 && (
              <div className="absolute mt-2 w-full bg-white border border-gray-200 rounded-xl shadow-lg z-10 overflow-hidden">
                {filteredData?.map(category => (
                  <div key={category.id} className="border-b border-gray-100 last:border-b-0">
                    <div className="px-4 py-3 bg-gray-50">
                      <span className="font-medium text-gray-800">{category.name}</span>
                    </div>
                    <ul className="py-2">
                      {category?.subCategories.map(sub => (
                        <li
                          key={sub.id}
                          className="px-4 py-2 hover:bg-blue-50 flex items-center justify-between cursor-pointer group"
                          onClick={() => {
                            setQuery('');
                            const targetPath = `/${sub?.id}`;
                            navigate(targetPath);
                            
                            // Update analytics for search navigation
                            setTimeout(() => {
                              const directRoute = directRouteMap[sub?.id];
                              if (directRoute) {
                                const { category, subcategory } = findCategoryAndSubcategory(
                                  directRoute.categoryId, 
                                  directRoute.subcategoryId
                                ) || {};
                                
                                if (category && subcategory) {
                                  const toolName = subcategory.name;
                                  const pageTitle = `${toolName} - Free Online Data Conversion Tool`;
                                  
                                  document.title = pageTitle;
                                  
                                  let description = "";
                                  if (toolName.includes(" to ")) {
                                    const [from, to] = toolName.split(" to ");
                                    description = `Convert ${from} data to ${to} format online for free. Easy-to-use ${from} to ${to} converter with no installation required.`;
                                  } else {
                                    description = `Work with ${category.name} data using our free online ${toolName} tool. No signup required, instant results.`;
                                  }
                                  
                                  const metaDescription = document.querySelector('meta[name="description"]');
                                  if (metaDescription) {
                                    metaDescription.setAttribute('content', description);
                                  }
                                  
                                  pushGTMEvent({
                                    event: 'page_view',
                                    page_title: pageTitle,
                                    page_location: window.location.origin + targetPath,
                                    page_path: targetPath,
                                    page_category: category.name,
                                    page_subcategory: subcategory.name,
                                    navigation_type: 'search_click'
                                  });
                                }
                              }
                            }, 200);
                          }}
                        >
                          <span className="text-gray-700 group-hover:text-blue-700 transition-colors">
                            {sub.name}
                          </span>
                          <ChevronRight size={16} className="text-gray-400 group-hover:text-blue-500 transition-colors" />
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Main content */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-extrabold text-gray-800 mb-4 tracking-tight">
              {currentCategory ? `${currentCategory.name}` : 'Data Conversion Tools'}
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {currentCategory
                ? `Transform your ${currentCategory.name.toLowerCase()} quickly and easily with our conversion tools.`
                : 'Simple, powerful tools to convert your data between formats. No installation required.'}
            </p>
          </div>

          {!currentCategory ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {categories.map(category => (
                <div
                  key={category.id}
                  className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm hover:shadow-md transition duration-300 cursor-pointer"
                  onClick={() => {
                    trackEvent('convert_page', {
                      event_category: 'conversion',
                      event_label: 'Navigate to Category',
                      page_location: window.location.href,
                    });

                    const targetPath = `/convert/${category.id}`;
                    navigate(targetPath);
                    
                    // Update analytics for category navigation
                    setTimeout(() => {
                      const pageTitle = `${category.name} Converters - Free Online Tools`;
                      document.title = pageTitle;
                      
                      const description = `Free online tools to convert ${category.name} data to various formats. Convert, format and transform your ${category.name} data with ease.`;
                      const metaDescription = document.querySelector('meta[name="description"]');
                      if (metaDescription) {
                        metaDescription.setAttribute('content', description);
                      }
                      
                      pushGTMEvent({
                        event: 'page_view',
                        page_title: pageTitle,
                        page_location: window.location.origin + targetPath,
                        page_path: targetPath,
                        page_category: category.name,
                        navigation_type: 'category_click'
                      });
                    }, 200);
                  }}
                >
                  <div className="p-6">
                    <h2 className="text-xl font-semibold text-gray-800 mb-2">{category.name}</h2>
                    <p className="text-gray-500 mb-4">{category.subCategories.length} conversion tools</p>

                    <div className="space-y-1 mb-4">
                      {category.subCategories.slice(0, 2).map(sub => (
                        <div key={sub.id} className="text-sm text-gray-600 flex items-center">
                          <div className="w-1 h-1 rounded-full bg-gray-400 mr-2"></div>
                          {sub.name}
                        </div>
                      ))}
                      {category.subCategories.length > 2 && (
                        <div className="text-sm text-gray-500">
                          +{category.subCategories.length - 2} more tools
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="bg-gray-50 px-6 py-3 flex justify-end">
                    <div className="flex items-center text-blue-600 font-medium text-sm group">
                      <span className="group-hover:underline">Explore</span>
                      <ArrowRight size={16} className="ml-1 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row max-w-6xl mx-auto">
              {/* Sidebar */}
              <div className="max-w-6xl mx-auto">
                <div className="mb-6">
                  <h2 className="text-2xl font-semibold text-gray-800">{currentCategory.name}</h2>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {currentCategory.subCategories.map(sub => (
                    <div
                      key={sub.id}
                      className="bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-200 cursor-pointer"
                      onClick={() => {
                        const targetPath = `/${sub.id}`;
                        navigate(targetPath);
                        
                        // Update analytics for subcategory navigation from category page
                        setTimeout(() => {
                          const directRoute = directRouteMap[sub.id];
                          if (directRoute) {
                            const { category, subcategory } = findCategoryAndSubcategory(
                              directRoute.categoryId, 
                              directRoute.subcategoryId
                            ) || {};
                            
                            if (category && subcategory) {
                              const toolName = subcategory.name;
                              const pageTitle = `${toolName} - Free Online Data Conversion Tool`;
                              
                              document.title = pageTitle;
                              
                              let description = "";
                              if (toolName.includes(" to ")) {
                                const [from, to] = toolName.split(" to ");
                                description = `Convert ${from} data to ${to} format online for free. Easy-to-use ${from} to ${to} converter with no installation required.`;
                              } else {
                                description = `Work with ${category.name} data using our free online ${toolName} tool. No signup required, instant results.`;
                              }
                              
                              const metaDescription = document.querySelector('meta[name="description"]');
                              if (metaDescription) {
                                metaDescription.setAttribute('content', description);
                              }
                              
                              pushGTMEvent({
                                event: 'page_view',
                                page_title: pageTitle,
                                page_location: window.location.origin + targetPath,
                                page_path: targetPath,
                                page_category: category.name,
                                page_subcategory: subcategory.name,
                                navigation_type: 'category_page_click'
                              });
                            }
                          }
                        }, 200);
                      }}
                    >
                      <div className="p-4 flex items-center justify-between">
                        <span className="text-gray-700 font-medium">{sub.name}</span>
                        <ChevronRight size={18} className="text-gray-400" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Features section */}
          {!currentCategory && (
            <div className="mt-24 text-center">
              <h2 className="text-2xl font-bold text-gray-800 mb-12">Why Choose Our Conversion Tools?</h2>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                  <div className="bg-blue-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <h3 className="font-semibold text-gray-800 mb-2">Fast & Efficient</h3>
                  <p className="text-gray-600 text-sm">Convert your data instantly with our optimized tools. No waiting or processing delays.</p>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                  <div className="bg-blue-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                  <h3 className="font-semibold text-gray-800 mb-2">Secure & Private</h3>
                  <p className="text-gray-600 text-sm">All conversions happen in your browser. Your data never leaves your device.</p>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                  <div className="bg-blue-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                  </div>
                  <h3 className="font-semibold text-gray-800 mb-2">100% Free</h3>
                  <p className="text-gray-600 text-sm">All our conversion tools are completely free to use with no hidden fees or subscriptions.</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) { // Mobile view (less than md breakpoint)
        setSidebarOpen(false);
      } else { // Desktop view
        setSidebarOpen(true);
      }
    };

    // Set initial state
    handleResize();

    // Add event listener
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <>
      <ScrollToTop />
      <SEO {...seoProps} />
      <div className="flex h-full bg-gray-100 relative">
        {/* Sidebar - controlled by sidebarOpen state */}
        <div
          className={`
    bg-white shadow-md overflow-y-auto
    transition-all duration-300 ease-in-out
    ${sidebarOpen ? 'w-64' : 'w-0 overflow-hidden'}
    ${sidebarOpen ? 'block' : 'hidden md:block'}
  `}
        >
          <div className="h-full">
            <nav className="py-2">
              {categoriesState.map(category => (
                <div key={category.id} className="mb-1">
                  <button
                    className={`
                      flex items-center justify-between w-full px-4 py-3 text-left
                      ${isCategorySelected(category) ? 'text-blue-600 font-medium' : 'text-gray-700'}
                      hover:bg-gray-50 focus:outline-none
                    `}
                    onClick={() => toggleCategory(category.id)}
                  >
                    <span className="text-base">{category.name}</span>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className={`
                        h-4 w-4 transition-transform duration-200 ease-in-out
                        ${category.isOpen ? 'transform rotate-180' : ''}
                      `}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {/* Subcategories with smooth height animation */}
                  <div
                    className={`
                      overflow-hidden transition-all duration-300 ease-in-out
                      ${Boolean(category.isOpen) ? 'max-h-full opacity-100' : 'max-h-0 opacity-0'}
                    `}
                  >
                    {category.subCategories.map(subCategory => (
                      <button
                        key={subCategory.id}
                        className={`
                          w-full text-left py-3 px-8 text-sm
                          ${isSubcategorySelected(category.id, subCategory.id)
                            ? 'text-blue-600 font-medium bg-blue-50'
                            : 'text-gray-700 hover:bg-gray-50'
                          }
                          focus:outline-none transition-colors
                        `}
                        onClick={() => navigateToSubcategory(category.id, subCategory.id, subCategory?.name)}
                      >
                        {subCategory.name}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </nav>
          </div>
        </div>

        {/* Main content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          <header className="bg-white shadow-sm z-10 relative">
            {/* Breadcrumbs */}
            {currentCategory && (
              <div className="flex items-center justify-between px-4 py-3 bg-gray-100 border-t border-b text-sm relative">

                {/* Toggle Button on left */}
                <button
                  onClick={() => {
                    trackEvent('convert_page', {
                      event_category: 'conversion',
                      event_label: 'Toggle Sidebar',
                      page_location: window.location.href,
                    });
                    setSidebarOpen(!sidebarOpen);
                  }}
                  aria-label={!sidebarOpen ? "Close sidebar" : "Open sidebar"}
                >
                  {sidebarOpen ? (
                    // Close Icon (X)
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  ) : (
                    // Menu Icon (Hamburger)
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="3" y1="12" x2="21" y2="12" />
                      <line x1="3" y1="6" x2="21" y2="6" />
                      <line x1="3" y1="18" x2="21" y2="18" />
                    </svg>
                  )}
                </button>

                {/* Breadcrumb Text */}
                <div className="flex items-center space-x-2 ml-4 flex-1 overflow-hidden">
                  <span className="text-gray-500 truncate animate-slide-in">CATEGORY</span>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                  <span className="text-gray-700 truncate">{currentCategory.name}</span>

                  {currentSubcategory && (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                      <span className="text-blue-600 font-medium truncate">{currentSubcategory.name}</span>
                    </>
                  )}
                </div>
              </div>
            )}
          </header>

          <main className="flex-1 overflow-auto p-6">
            {renderMainContent()}
          </main>
        </div>
      </div>
    </>
  );
};

export default ConvertData;